import * as React from 'react';
import { Check } from 'lucide-react';

export interface Step {
  label: string;
  completed: boolean;
}

interface ProgressBarProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (index: number) => void;
}

/**
 * Step header + progress indicator for multi-step flows (invitation/access,
 * offer creation, …). Ported off baseui/styletron to Tailwind + design
 * tokens — same props, same behavior (SAM-199).
 */
export const ProgressBar = ({ steps, currentStep, onStepClick }: ProgressBarProps) => {
  const progress = steps.length > 1 ? (currentStep / (steps.length - 1)) * 100 : 0;

  return (
    <div className="w-full py-6 mb-4">
      <div className="relative flex items-center justify-between max-w-3xl mx-auto px-4 sm:px-8">
        {/* Every step is the same fixed width, so the line can run exactly from the first dot's
            centre to the last one's (padding + half a step) instead of from the label's edge.
            On a phone the steps are 4rem wide with 1rem padding (four of them fit in 375 px), so the
            line starts at 3rem; from `sm` up it is 2rem + 3rem as before. */}
        <div
          className="absolute h-[2px] top-4 -translate-y-1/2 left-[3rem] right-[3rem] sm:left-[5rem] sm:right-[5rem]"
          style={{ background: 'var(--line)' }}
        >
          <div
            className="absolute h-full transition-all duration-300"
            style={{ width: `${progress}%`, background: 'var(--tenant-primary, #111827)' }}
          />
        </div>
        {steps.map((step, index) => {
          const active = currentStep === index;
          return (
            <div
              key={step.label}
              className="relative flex flex-col items-center gap-2 w-16 sm:w-24 shrink-0 text-center"
              style={{ cursor: step.completed ? 'pointer' : 'default' }}
              onClick={() => step.completed && onStepClick?.(index)}
            >
              <div
                className="flex items-center justify-center rounded-full text-sm font-medium transition-all"
                style={{
                  width: 32,
                  height: 32,
                  background: step.completed
                    ? 'var(--tenant-primary, #111827)'
                    : active
                      ? '#fff'
                      : 'var(--line)',
                  border: active ? '2px solid var(--tenant-primary, #111827)' : 'none',
                  color: step.completed ? '#fff' : active ? 'var(--tenant-primary, #111827)' : '#fff',
                }}
              >
                {step.completed ? <Check size={16} /> : index + 1}
              </div>
              <span
                className="text-xs sm:text-sm transition-all"
                style={{
                  color: active ? 'var(--ink-900)' : 'var(--ink-500)',
                  fontWeight: active ? 500 : 400,
                }}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
